/**
 * Países y reglas de documento de identidad para el acceso al portal.
 * Debe mantenerse alineado con el backend: server/src/utils/dni.utils.js
 * (misma normalización: minúsculas, sin puntos; Chile "NNNNNNNN-D" con k minúscula).
 *
 * 'XX' = "Otro" (país genérico, regla de formato laxa).
 */

export const DOCUMENT_COUNTRIES = [
  { code: 'CL', name: 'Chile', docLabel: 'RUT', pattern: /^\d{7,8}-[\dk]$/ },
  { code: 'AR', name: 'Argentina', docLabel: 'DNI', pattern: /^\d{7,8}$/ },
  { code: 'PE', name: 'Perú', docLabel: 'DNI', pattern: /^\d{8}$/ },
  { code: 'CO', name: 'Colombia', docLabel: 'Cédula', pattern: /^\d{6,10}$/ },
  { code: 'EC', name: 'Ecuador', docLabel: 'Cédula', pattern: /^\d{10}$/ },
  { code: 'VE', name: 'Venezuela', docLabel: 'Cédula', pattern: /^\d{6,9}$/ },
  { code: 'UY', name: 'Uruguay', docLabel: 'CI', pattern: /^\d{6,8}$/ },
  { code: 'PY', name: 'Paraguay', docLabel: 'CI', pattern: /^\d{5,8}$/ },
  { code: 'BO', name: 'Bolivia', docLabel: 'CI', pattern: /^\d{5,10}$/ },
  { code: 'MX', name: 'México', docLabel: 'CURP', pattern: /^[a-z0-9]{10,18}$/ },
  { code: 'BR', name: 'Brasil', docLabel: 'CPF', pattern: /^\d{11}$/ },
  { code: 'ES', name: 'España', docLabel: 'DNI/NIE', pattern: /^[a-z]?\d{7,8}[a-z]$/ },
  { code: 'XX', name: null, docLabel: null, pattern: /^[a-z0-9]{5,15}$/ }, // "Otro" (i18n)
];

/**
 * Nacionalidades del acceso al portal y del formulario de las masivas, con el
 * país de su documento (mismo mapa que el backend: utils/nacionalidad.utils.js).
 * Los países sin regla propia arriba (HT, DO, CU) usan el formato libre de "Otro".
 */
export const NATIONALITY_OPTIONS = [
  { label: 'Chilena', code: 'CL' },
  { label: 'Venezolana', code: 'VE' },
  { label: 'Peruana', code: 'PE' },
  { label: 'Colombiana', code: 'CO' },
  { label: 'Haitiana', code: 'HT' },
  { label: 'Boliviana', code: 'BO' },
  { label: 'Argentina', code: 'AR' },
  { label: 'Ecuatoriana', code: 'EC' },
  { label: 'Dominicana', code: 'DO' },
  { label: 'Cubana', code: 'CU' },
  { label: 'Brasileña', code: 'BR' },
  { label: 'Paraguaya', code: 'PY' },
  { label: 'Uruguaya', code: 'UY' },
  { label: 'Mexicana', code: 'MX' },
  { label: 'Española', code: 'ES' },
];

export const getCountry = (code) =>
  DOCUMENT_COUNTRIES.find((c) => c.code === code) || null;

/** Normaliza el documento con la misma convención del backend. */
export const normalizeDocument = (value, countryCode) => {
  if (!value) return '';
  const lowered = value.toLowerCase().trim();

  if (countryCode === 'CL') {
    const cleaned = lowered.replace(/[^0-9k]/g, '');
    if (cleaned.length < 2) return cleaned;
    return `${cleaned.slice(0, -1)}-${cleaned.slice(-1)}`;
  }

  return lowered.replace(/[^a-z0-9]/g, '');
};

/** Valida dígito verificador de RUT chileno normalizado (módulo 11). */
const isValidRutChile = (normalized) => {
  if (!/^\d{7,8}-[\dk]$/.test(normalized)) return false;
  const [rutNumber, rutDV] = normalized.split('-');

  let sum = 0;
  let multiplier = 2;
  for (let i = rutNumber.length - 1; i >= 0; i--) {
    sum += parseInt(rutNumber.charAt(i), 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedDV =
    remainder === 0 ? '0' : remainder === 1 ? 'k' : String(11 - remainder);

  return rutDV === calculatedDV;
};

/** Valida un documento según el país. Devuelve { valid, normalized }. */
export const validateDocument = (value, countryCode) => {
  const normalized = normalizeDocument(value, countryCode);
  if (!normalized) return { valid: false, normalized };

  const country = getCountry(countryCode) || getCountry('XX');
  if (!country.pattern.test(normalized)) return { valid: false, normalized };

  if (countryCode === 'CL' && !isValidRutChile(normalized)) {
    return { valid: false, normalized };
  }

  return { valid: true, normalized };
};
